import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { Params, Router, RouterModule } from '@angular/router';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { ProductVariant } from '@app/akita/api/products/models/product-variant.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { PictureComponent } from '@app/shared/components/picture/picture.component';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { TranslocoModule } from '@ngneat/transloco';
import { Observable, of, Subscription, tap } from 'rxjs';
import { ProductJsonLdComponent } from '../product-json-ld/product-json-ld.component';

@Component({
  selector: 'app-product-keyboard-variants',
  templateUrl: './product-keyboard-variants.template.html',
  styleUrls: ['./product-keyboard-variants.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    CommonModule,
    TranslocoModule,
    FlexLayoutModule,
    RouterModule,
    PictureComponent,
    ProductJsonLdComponent,
    PopsySkeletonComponent,
  ],
})
export class ProductKeyboardVariantsComponent implements OnChanges, OnDestroy {
  @Input() public id?: string | null;
  @Input() public model?: string | null;
  @Input() public showLabel?: boolean | null = true;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;

  public productOptions$: Observable<ProductSearchFilterOptions | null>;

  public variants: Array<ProductVariant>;

  public keyboardVariants$: Observable<Array<ProductVariant>>;
  public findingKeyboardVariants$: Observable<boolean>;
  public keyboardVariantsError$: Observable<ApiError | null>;

  public readonly queryParamsToKeep$: Observable<Params>;

  private readonly subscriptions: Subscription;

  constructor(
    private readonly router: Router,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaScreenQuery: AkitaScreenQuery
  ) {
    this.subscriptions = new Subscription();

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.productOptions$ = this.akitaProductsQuery.selectAvailableProductOptions(
      this.model || ''
    );

    this.variants = new Array(0);

    this.keyboardVariants$ = of([]);

    this.findingKeyboardVariants$ = of(false);
    this.keyboardVariantsError$ = of(null);

    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.id) {
      this.refresh();
    }
  }

  private refresh(): void {
    this.keyboardVariants$ = this.akitaProductsQuery
      .selectProductVariants(this.model)
      .pipe(tap((variants: Array<ProductVariant>) => (this.variants = [...variants])));

    this.findingKeyboardVariants$ =
      this.akitaProductsQuery.selectIsSearchingProductVariants(this.id);

    this.keyboardVariantsError$ =
      this.akitaProductsQuery.selectErrorSearchingProductVariants(this.id);

    this.productOptions$ = this.akitaProductsQuery.selectAvailableProductOptions(
      this.model || ''
    );

    this.changeDetector.markForCheck();
  }

  public isVariantAvailable(keyboard: string): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    return Boolean(
      this.akitaProductsQuery.getMatchingKeyboardVariant(
        this.model,
        currentVariant,
        keyboard
      )
    );
  }

  public getVariantFragment(keyboard: string | null): Array<string> {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingKeyboardVariant(
      this.model,
      currentVariant,
      keyboard
    );

    const out = this.akitaRouterQuery.getProductUrlFragment(
      aux?.id || '',
      aux?.title || ''
    );

    return out;
  }

  public isVariantSelected(keyboard: string): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    return currentVariant?.keyboard === keyboard;
  }

  public switchVariant(code: string | null): void {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    const aux = this.akitaProductsQuery.getMatchingKeyboardVariant(
      this.model,
      currentVariant,
      code
    );
    if (aux?.inStock) {
      const fragment = this.akitaRouterQuery.getProductUrlFragment(aux.id);

      this.subscriptions.add(
        this.akitaProductsService.getProduct(aux.id).subscribe({
          next: () => {
            this.router
              .navigate(fragment, { queryParamsHandling: 'merge' })
              .catch((error: unknown) => {
                console.warn(error);
              });
          },
          error: () => {},
        })
      );
    }
  }

  public trackByProduct(index: number, value?: any | null): string {
    return `${value?.id || index}`;
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
